'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import DailyIframe, {
    DailyCall,
    DailyEventObjectParticipant,
    DailyEventObjectParticipantLeft,
} from '@daily-co/daily-js'
import openSocket, { Socket } from 'socket.io-client'
import { toast } from 'react-toastify'
import {
    MdVideocam,
    MdVideocamOff,
    MdMic,
    MdMicOff,
    MdChat,
    MdSend,
    MdClose,
    MdCheckCircle,
    MdError,
    MdAccessTime,
} from 'react-icons/md'
import {
    getPublicTeleconsultation,
    joinPublicTeleconsultation,
    getPublicChatMessages,
    sendPublicChatMessage,
    PublicTeleconsultationRoom,
} from '@/lib/api/teleconsultation/teleconsultation-public-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

const baseUrl = import.meta.env.VITE_API_BASE_URL

type PageState =
    | 'loading'
    | 'invalid'
    | 'waiting-room'
    | 'pre-call'
    | 'in-call'
    | 'ended'

interface ChatMessage {
    id?: number
    senderRole: string
    senderName: string
    message: string
    createdAt: string
}

export default function PatientTeleconsultationPage() {
    const { token } = useParams<{ token: string }>()

    const [pageState, setPageState] = useState<PageState>('loading')
    const [room, setRoom] = useState<PublicTeleconsultationRoom | null>(null)
    const [errorMessage, setErrorMessage] = useState('')
    const [patientName, setPatientName] = useState('')
    const [joining, setJoining] = useState(false)

    // Controles de midia
    const [isCameraOn, setIsCameraOn] = useState(true)
    const [isMicOn, setIsMicOn] = useState(true)
    const [doctorConnected, setDoctorConnected] = useState(false)

    // Chat
    const [showChat, setShowChat] = useState(false)
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
    const [chatMessage, setChatMessage] = useState('')
    const [sendingMessage, setSendingMessage] = useState(false)
    const [unreadCount, setUnreadCount] = useState(0)

    // Refs
    const showChatRef = useRef(false)
    const callObjectRef = useRef<DailyCall | null>(null)
    const socketRef = useRef<Socket | null>(null)
    const remoteVideoRef = useRef<HTMLVideoElement>(null)
    const localVideoRef = useRef<HTMLVideoElement>(null)
    const previewVideoRef = useRef<HTMLVideoElement>(null)
    const previewStreamRef = useRef<MediaStream | null>(null)
    const chatEndRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (token) {
            loadRoom()
        }
        return () => {
            destroyDaily()
            destroySocket()
            stopPreview()
        }
    }, [token])

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [chatMessages])

    // Sincronizar ref do chat e zerar contador ao abrir
    useEffect(() => {
        showChatRef.current = showChat
        if (showChat) {
            setUnreadCount(0)
        }
    }, [showChat])

    // Conectar socket standalone para paciente (sem JWT)
    const initSocket = useCallback(
        (teleconsultationId: number) => {
            if (socketRef.current || !baseUrl || !token) return

            const sock = openSocket(baseUrl, {
                transports: ['websocket', 'polling'],
                auth: {
                    teleconsultationToken: token,
                },
                reconnectionAttempts: 5,
                reconnectionDelay: 2000,
            })

            socketRef.current = sock

            sock.on('connect', () => {
                sock.emit('teleconsultation:join', { teleconsultationId })
            })

            sock.on(
                'teleconsultation:chat-message',
                (data: {
                    teleconsultationId: number
                    message: ChatMessage
                }) => {
                    if (data.teleconsultationId === teleconsultationId) {
                        setChatMessages(prev => {
                            const exists = prev.some(
                                m => m.id === data.message.id
                            )
                            if (exists) return prev
                            return [...prev, data.message]
                        })
                        // Se o chat está fechado e a mensagem é do profissional
                        if (
                            !showChatRef.current &&
                            data.message.senderRole !== 'PATIENT'
                        ) {
                            setUnreadCount(prev => prev + 1)
                            toast.info(
                                `${data.message.senderName}: ${data.message.message}`,
                                { autoClose: 4000 }
                            )
                        }
                    }
                }
            )

            sock.on(
                'teleconsultation:status-changed',
                (data: {
                    teleconsultationId: number
                    status: string
                }) => {
                    if (data.teleconsultationId === teleconsultationId) {
                        if (data.status === 'COMPLETED') {
                            destroyDaily()
                            setPageState('ended')
                        }
                    }
                }
            )

            sock.on(
                'teleconsultation:participant-joined',
                (data: {
                    teleconsultationId: number
                    role: string
                    name: string
                }) => {
                    if (
                        data.teleconsultationId === teleconsultationId &&
                        data.role === 'PROFESSIONAL'
                    ) {
                        setDoctorConnected(true)
                    }
                }
            )

            sock.on(
                'teleconsultation:participant-left',
                (data: {
                    teleconsultationId: number
                    role: string
                    name: string
                }) => {
                    if (
                        data.teleconsultationId === teleconsultationId &&
                        data.role === 'PROFESSIONAL'
                    ) {
                        setDoctorConnected(false)
                    }
                }
            )
        },
        [token]
    )

    const destroySocket = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.disconnect()
            socketRef.current = null
        }
    }, [])

    const loadRoom = async () => {
        if (!token) return

        const response = await getPublicTeleconsultation(token)
        if (!response.success) {
            setErrorMessage(
                response.error ||
                'Link de teleconsulta inválido ou expirado.'
            )
            setPageState('invalid')
            return
        }

        const tcRoom = response.result
        setRoom(tcRoom)
        setPatientName(tcRoom.clientName || tcRoom.client?.name || '')

        switch (tcRoom.status) {
            case 'COMPLETED':
            case 'CANCELLED':
            case 'EXPIRED':
                setPageState('ended')
                break
            case 'IN_PROGRESS':
            case 'WAITING_PATIENT':
                setPageState('pre-call')
                startPreview()
                initSocket(tcRoom.id)
                break
            default:
                setPageState('waiting-room')
                initSocket(tcRoom.id)
                break
        }
    }

    // Preview de câmera/microfone para pre-call
    const startPreview = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true,
            })
            previewStreamRef.current = stream
            if (previewVideoRef.current) {
                previewVideoRef.current.srcObject = stream
            }
        } catch (error) {
            console.warn('[Preview] Não foi possível acessar câmera/microfone:', error)
        }
    }

    const stopPreview = () => {
        if (previewStreamRef.current) {
            previewStreamRef.current.getTracks().forEach(t => t.stop())
            previewStreamRef.current = null
        }
        if (previewVideoRef.current) {
            previewVideoRef.current.srcObject = null
        }
    }

    const togglePreviewCamera = () => {
        if (previewStreamRef.current) {
            previewStreamRef.current
                .getVideoTracks()
                .forEach(t => (t.enabled = !t.enabled))
        }
        setIsCameraOn(prev => !prev)
    }

    const togglePreviewMic = () => {
        if (previewStreamRef.current) {
            previewStreamRef.current
                .getAudioTracks()
                .forEach(t => (t.enabled = !t.enabled))
        }
        setIsMicOn(prev => !prev)
    }

    const handleJoin = async () => {
        if (!token || !room) return

        setJoining(true)
        try {
            const response = await joinPublicTeleconsultation(
                token,
                patientName || undefined
            )
            if (response.success) {
                stopPreview()
                await initializeDaily(room)
                await loadChat()
                setPageState('in-call')
            } else {
                setErrorMessage(
                    response.error || 'Erro ao entrar na teleconsulta'
                )
            }
        } catch {
            setErrorMessage('Erro ao entrar na teleconsulta')
        } finally {
            setJoining(false)
        }
    }

    const initializeDaily = async (tc: PublicTeleconsultationRoom) => {
        if (callObjectRef.current) return
        if (!tc.dailyRoomUrl || !tc.patientDailyToken) {
            setErrorMessage('Dados da sala de vídeo indisponíveis')
            return
        }

        try {
            const callObject = DailyIframe.createCallObject({
                audioSource: isMicOn,
                videoSource: isCameraOn,
            })

            callObjectRef.current = callObject

            // Eventos do Daily.co
            callObject.on(
                'participant-joined',
                (event?: DailyEventObjectParticipant) => {
                    if (event?.participant && !event.participant.local) {
                        setDoctorConnected(true)
                        attachRemoteVideo(event.participant)
                    }
                }
            )

            callObject.on(
                'participant-updated',
                (event?: DailyEventObjectParticipant) => {
                    if (event?.participant && !event.participant.local) {
                        attachRemoteVideo(event.participant)
                    }
                    if (event?.participant?.local) {
                        attachLocalVideo(event.participant)
                    }
                }
            )

            callObject.on(
                'participant-left',
                (event?: DailyEventObjectParticipantLeft) => {
                    if (event?.participant && !event.participant.local) {
                        setDoctorConnected(false)
                        if (remoteVideoRef.current) {
                            remoteVideoRef.current.srcObject = null
                        }
                    }
                }
            )

            callObject.on('error', event => {
                console.error('[Daily] Erro:', event)
            })

            // Entrar na sala
            await callObject.join({
                url: tc.dailyRoomUrl,
                token: tc.patientDailyToken,
            })

            // Attach vídeo local
            const localParticipant = callObject.participants().local
            if (localParticipant) {
                attachLocalVideo(localParticipant)
            }

            // Verificar participantes remotos existentes
            const participants = callObject.participants()
            for (const [key, participant] of Object.entries(participants)) {
                if (key !== 'local' && participant) {
                    setDoctorConnected(true)
                    attachRemoteVideo(participant)
                }
            }
        } catch (error: any) {
            console.error('[Daily] Erro ao inicializar:', error)
            setErrorMessage(
                'Erro ao conectar na sala de vídeo: ' + error.message
            )
        }
    }

    const attachRemoteVideo = (participant: any) => {
        if (!remoteVideoRef.current) return
        const videoTrack = participant.tracks?.video
        if (videoTrack?.track) {
            const stream = new MediaStream([videoTrack.track])
            remoteVideoRef.current.srcObject = stream
        }
    }

    const attachLocalVideo = (participant: any) => {
        if (!localVideoRef.current) return
        const videoTrack = participant.tracks?.video
        if (videoTrack?.track) {
            const stream = new MediaStream([videoTrack.track])
            localVideoRef.current.srcObject = stream
        }
    }

    const destroyDaily = useCallback(() => {
        if (callObjectRef.current) {
            callObjectRef.current.leave().catch(() => { })
            callObjectRef.current.destroy().catch(() => { })
            callObjectRef.current = null
        }
    }, [])

    const toggleCamera = () => {
        const call = callObjectRef.current
        if (!call) return
        call.setLocalVideo(!isCameraOn)
        setIsCameraOn(prev => !prev)
    }

    const toggleMic = () => {
        const call = callObjectRef.current
        if (!call) return
        call.setLocalAudio(!isMicOn)
        setIsMicOn(prev => !prev)
    }

    const handleLeave = () => {
        destroyDaily()
        destroySocket()
        setPageState('ended')
    }

    const loadChat = async () => {
        if (!token) return
        const response = await getPublicChatMessages(token)
        if (response.success) {
            setChatMessages(response.result || [])
        }
    }

    const handleSendMessage = async () => {
        if (!token || !chatMessage.trim()) return

        setSendingMessage(true)
        try {
            const response = await sendPublicChatMessage(
                token,
                chatMessage.trim()
            )
            if (response.success) {
                setChatMessage('')
            }
        } finally {
            setSendingMessage(false)
        }
    }

    const formatScheduledAt = (date: string) => {
        return format(
            new Date(date),
            "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
            { locale: ptBR }
        )
    }

    // Tela de carregamento
    if (pageState === 'loading') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="loading loading-spinner loading-lg text-violet-600 mb-4"></div>
                    <p className="text-gray-500">
                        Carregando teleconsulta...
                    </p>
                </div>
            </div>
        )
    }

    // Tela de link inválido/expirado
    if (pageState === 'invalid') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                        <MdError className="text-3xl text-red-500" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Link Inválido
                    </h1>
                    <p className="text-gray-500 mb-6">{errorMessage}</p>
                    <p className="text-sm text-gray-400">
                        Se você acredita que isso é um erro, entre em contato
                        com a clínica que agendou sua teleconsulta.
                    </p>
                </div>
            </div>
        )
    }

    // Tela de teleconsulta encerrada
    if (pageState === 'ended') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                        <MdCheckCircle className="text-3xl text-green-500" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Teleconsulta Encerrada
                    </h1>
                    <p className="text-gray-500 mb-4">
                        Obrigado por utilizar nosso serviço de teleconsulta.
                    </p>
                    {room?.company?.name && (
                        <p className="text-sm text-gray-400">
                            {room.company.name}
                        </p>
                    )}
                </div>
            </div>
        )
    }

    // Sala de espera (médico ainda não iniciou)
    if (pageState === 'waiting-room') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center px-4">
                <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg p-8">
                    <div className="text-center mb-8">
                        {room?.company?.name && (
                            <p className="text-sm text-violet-600 font-medium mb-2">
                                {room.company.name}
                            </p>
                        )}
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-violet-100 flex items-center justify-center">
                            <MdVideocam className="text-3xl text-violet-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            Teleconsulta Agendada
                        </h1>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3">
                        <div className="flex items-center gap-3">
                            <MdAccessTime className="text-violet-500" />
                            <div>
                                <p className="text-sm text-gray-500">
                                    Data e Horário
                                </p>
                                <p className="font-medium text-gray-900">
                                    {room?.scheduledAt
                                        ? formatScheduledAt(room.scheduledAt)
                                        : 'N/A'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <MdVideocam className="text-violet-500" />
                            <div>
                                <p className="text-sm text-gray-500">
                                    Profissional
                                </p>
                                <p className="font-medium text-gray-900">
                                    {room?.professional?.name || 'N/A'}
                                </p>
                            </div>
                        </div>
                        {room?.professional?.specialties &&
                            room.professional.specialties.length > 0 && (
                                <div className="flex items-center gap-3">
                                    <div className="w-5" />
                                    <div>
                                        <p className="text-sm text-gray-500">
                                            Especialidade
                                        </p>
                                        <p className="font-medium text-gray-900">
                                            {room.professional.specialties
                                                .map(
                                                    s =>
                                                        s.specialty.name
                                                )
                                                .join(', ')}
                                        </p>
                                    </div>
                                </div>
                            )}
                    </div>

                    <div className="bg-amber-50 rounded-xl p-4 mb-6 border border-amber-200">
                        <p className="text-sm text-amber-800">
                            A teleconsulta ainda não foi iniciada pelo
                            profissional. Por favor, aguarde ou retorne no
                            horário agendado.
                        </p>
                    </div>

                    <button
                        onClick={loadRoom}
                        className="w-full py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 transition-colors"
                    >
                        Verificar Novamente
                    </button>
                </div>
            </div>
        )
    }

    // Pre-call (teste de câmera/microfone)
    if (pageState === 'pre-call') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
                <div className="w-full max-w-5xl bg-white rounded-2xl shadow-lg !max-w-[1300px] p-6 lg:p-8 my-auto flex flex-col lg:flex-row gap-6 lg:gap-8">

                    {/* Lado esquerdo - Preview de vídeo + controles de mídia */}
                    <div className="flex-1 flex flex-col min-w-0">
                        {/* Preview de vídeo real */}
                        <div className="w-full aspect-video bg-gray-900 rounded-xl flex items-center justify-center overflow-hidden">
                            {isCameraOn ? (
                                <video
                                    ref={previewVideoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <MdVideocamOff className="text-4xl text-white/50" />
                            )}
                        </div>

                        {/* Controles de preview */}
                        <div className="flex justify-center gap-4 mt-4">
                            <button
                                onClick={togglePreviewCamera}
                                className={`w-12 h-12 rounded-full flex items-center justify-center ${isCameraOn
                                    ? 'bg-gray-100 text-gray-700'
                                    : 'bg-red-100 text-red-600'
                                    }`}
                                title={
                                    isCameraOn
                                        ? 'Desativar câmera'
                                        : 'Ativar câmera'
                                }
                            >
                                {isCameraOn ? (
                                    <MdVideocam className="text-xl" />
                                ) : (
                                    <MdVideocamOff className="text-xl" />
                                )}
                            </button>
                            <button
                                onClick={togglePreviewMic}
                                className={`w-12 h-12 rounded-full flex items-center justify-center ${isMicOn
                                    ? 'bg-gray-100 text-gray-700'
                                    : 'bg-red-100 text-red-600'
                                    }`}
                                title={
                                    isMicOn
                                        ? 'Desativar microfone'
                                        : 'Ativar microfone'
                                }
                            >
                                {isMicOn ? (
                                    <MdMic className="text-xl" />
                                ) : (
                                    <MdMicOff className="text-xl" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Lado direito - Informações e botão de entrar */}
                    <div className="w-full lg:w-80 flex flex-col justify-center shrink-0">
                        <div className="text-center lg:text-left mb-6">
                            {room?.company?.name && (
                                <p className="text-sm text-violet-600 font-medium mb-1">
                                    {room.company.name}
                                </p>
                            )}
                            <h2 className="text-xl font-bold text-gray-900 mb-1">
                                Preparar para Entrar
                            </h2>
                            <p className="text-sm text-gray-500">
                                Verifique sua câmera e microfone antes de entrar na teleconsulta.
                            </p>
                        </div>

                        {/* Nome do paciente */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Seu nome
                            </label>
                            <input
                                type="text"
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                placeholder="Digite seu nome..."
                                value={patientName}
                                onChange={e => setPatientName(e.target.value)}
                            />
                        </div>

                        {room?.recordingEnabled && (
                            <div className="bg-amber-50 rounded-xl p-4 mb-4 border border-amber-200">
                                <p className="text-sm text-amber-800">
                                    Esta teleconsulta poderá ser gravada para
                                    fins de registro médico. Ao entrar, você
                                    concorda com a gravação.
                                </p>
                            </div>
                        )}

                        {errorMessage && (
                            <div className="bg-red-50 rounded-xl p-4 mb-4 border border-red-200">
                                <p className="text-sm text-red-800">
                                    {errorMessage}
                                </p>
                            </div>
                        )}

                        <button
                            onClick={handleJoin}
                            disabled={joining}
                            className="w-full py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {joining ? (
                                <>
                                    <div className="loading loading-spinner loading-sm"></div>
                                    Entrando...
                                </>
                            ) : (
                                <>
                                    <MdVideocam className="text-xl" />
                                    Entrar na Teleconsulta
                                </>
                            )}
                        </button>
                    </div>

                </div>
            </div>
        )
    }

    // Em chamada
    return (
        <div className="flex h-screen bg-gray-900 overflow-hidden">
            {/* Área de vídeo */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <div className="bg-gray-800 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-violet-500 flex items-center justify-center">
                            <MdVideocam className="text-white text-sm" />
                        </div>
                        <div>
                            <p className="text-white text-sm font-medium">
                                {room?.professional?.name || 'Profissional'}
                            </p>
                            <p className="text-gray-400 text-xs">
                                {room?.company?.name || ''}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div
                            className={`w-2 h-2 rounded-full ${doctorConnected
                                ? 'bg-green-400'
                                : 'bg-yellow-400 animate-pulse'
                                }`}
                        />
                        <span className="text-gray-400 text-xs">
                            {doctorConnected
                                ? 'Profissional conectado'
                                : 'Aguardando profissional...'}
                        </span>
                    </div>
                </div>

                {/* Vídeo */}
                <div className="flex-1 min-h-0 relative flex items-center justify-center overflow-hidden">
                    {/* Vídeo remoto (profissional) */}
                    <div className="w-full h-full flex items-center justify-center">
                        {!doctorConnected ? (
                            <div className="text-center">
                                <div className="loading loading-dots loading-lg text-violet-400 mb-4"></div>
                                <h3 className="text-lg font-semibold text-white/80 mb-2">
                                    Aguardando profissional...
                                </h3>
                                <p className="text-sm text-white/50">
                                    O profissional entrará na sala em
                                    instantes.
                                </p>
                            </div>
                        ) : (
                            <video
                                ref={remoteVideoRef}
                                autoPlay
                                playsInline
                                className="w-full h-full object-cover bg-neutral rounded-lg"
                            />
                        )}
                    </div>

                    {/* Vídeo local (paciente) - miniatura */}
                    <div className="absolute bottom-4 right-4 w-40 h-28 bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden">
                        {isCameraOn ? (
                            <video
                                ref={localVideoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/30">
                                <MdVideocamOff className="text-2xl" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Controles */}
                <div className="bg-gray-800 px-4 py-4 flex items-center justify-center gap-4 shrink-0">
                    <button
                        onClick={toggleMic}
                        className={`w-12 h-12 rounded-full flex items-center justify-center ${isMicOn
                            ? 'bg-gray-600 text-white hover:bg-gray-500'
                            : 'bg-red-500 text-white hover:bg-red-600'
                            }`}
                        title={
                            isMicOn
                                ? 'Desativar microfone'
                                : 'Ativar microfone'
                        }
                    >
                        {isMicOn ? (
                            <MdMic className="text-xl" />
                        ) : (
                            <MdMicOff className="text-xl" />
                        )}
                    </button>

                    <button
                        onClick={toggleCamera}
                        className={`w-12 h-12 rounded-full flex items-center justify-center ${isCameraOn
                            ? 'bg-gray-600 text-white hover:bg-gray-500'
                            : 'bg-red-500 text-white hover:bg-red-600'
                            }`}
                        title={
                            isCameraOn
                                ? 'Desativar câmera'
                                : 'Ativar câmera'
                        }
                    >
                        {isCameraOn ? (
                            <MdVideocam className="text-xl" />
                        ) : (
                            <MdVideocamOff className="text-xl" />
                        )}
                    </button>

                    {room?.enableChat && (
                        <div className="relative">
                            <button
                                onClick={() => setShowChat(prev => !prev)}
                                className={`w-12 h-12 rounded-full flex items-center justify-center ${showChat
                                    ? 'bg-violet-500 text-white'
                                    : 'bg-gray-600 text-white hover:bg-gray-500'
                                    }`}
                                title="Chat"
                            >
                                <MdChat className="text-xl" />
                            </button>
                            {unreadCount > 0 && !showChat && (
                                <span className="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold px-1">
                                    {unreadCount > 99
                                        ? '99+'
                                        : unreadCount}
                                </span>
                            )}
                        </div>
                    )}

                    <button
                        onClick={handleLeave}
                        className="w-14 h-12 rounded-full bg-red-500 text-white hover:bg-red-600 flex items-center justify-center"
                        title="Sair da teleconsulta"
                    >
                        <MdClose className="text-xl" />
                    </button>
                </div>
            </div>

            {/* Chat lateral */}
            {showChat && room?.enableChat && (
                <div className="w-72 bg-gray-800 border-l border-gray-700 flex flex-col">
                    <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                        <h3 className="text-white text-sm font-semibold">
                            Chat
                        </h3>
                        <button
                            onClick={() => setShowChat(false)}
                            className="text-gray-400 hover:text-white"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        {chatMessages.length === 0 && (
                            <p className="text-center text-sm text-gray-500 py-8">
                                Nenhuma mensagem ainda
                            </p>
                        )}
                        {chatMessages.map((msg, index) => (
                            <div
                                key={msg.id || index}
                                className={`flex flex-col ${msg.senderRole === 'PATIENT'
                                    ? 'items-end'
                                    : 'items-start'
                                    }`}
                            >
                                <div
                                    className={`max-w-[90%] rounded-lg p-2 ${msg.senderRole === 'PATIENT'
                                        ? 'bg-violet-600 text-white'
                                        : 'bg-gray-700 text-white'
                                        }`}
                                >
                                    <p className="text-xs font-semibold mb-0.5 opacity-70">
                                        {msg.senderName}
                                    </p>
                                    <p className="text-sm">{msg.message}</p>
                                </div>
                                {msg.createdAt && (
                                    <p className="text-xs text-gray-500 mt-0.5 px-1">
                                        {format(
                                            new Date(msg.createdAt),
                                            'HH:mm',
                                            { locale: ptBR }
                                        )}
                                    </p>
                                )}
                            </div>
                        ))}
                        <div ref={chatEndRef} />
                    </div>

                    <div className="p-3 border-t border-gray-700">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                                placeholder="Mensagem..."
                                value={chatMessage}
                                onChange={e =>
                                    setChatMessage(e.target.value)
                                }
                                onKeyDown={e => {
                                    if (
                                        e.key === 'Enter' &&
                                        !e.shiftKey
                                    ) {
                                        e.preventDefault()
                                        handleSendMessage()
                                    }
                                }}
                                disabled={sendingMessage}
                            />
                            <button
                                onClick={handleSendMessage}
                                disabled={
                                    sendingMessage ||
                                    !chatMessage.trim()
                                }
                                className="w-10 h-10 rounded-lg bg-violet-600 text-white flex items-center justify-center hover:bg-violet-700 disabled:opacity-50"
                            >
                                <MdSend />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
